import moment from "moment-timezone";
import type { SelectableOption } from "@core/features/actions";
import { calculateTimeslotId } from "@core/features/timeslots";

/**
 * Determine which selectableOptions are selected based on saved time slot IDs.
 *
 * Used in repeating and repeating-dropdown modes where options with the same
 * time range are grouped together. Each option represents a specific date/time,
 * and this function checks if any of the option's slots overlap with saved slots.
 *
 * @param selectableOptions - Array of repeating options to check
 * @param savedSlotIds - Array of saved weekly slot IDs (0-335)
 * @returns Array of options that overlap with saved slots
 *
 * @example
 * ```typescript
 * const options = [
 *   { date: "2026-01-12", startHour: 14, startMinute: 0, endHour: 16, endMinute: 0, duration: 2 },
 *   { date: "2026-01-13", startHour: 14, startMinute: 0, endHour: 16, endMinute: 0, duration: 2 },
 * ];
 * const savedSlots = [76, 77, 78, 79]; // Monday 2-4pm slots
 *
 * const selected = getSelectedRepeatingOptionsFromSlots(options, savedSlots);
 * // Returns the option for Monday (2026-01-12) if it matches
 * ```
 */
export function getSelectedRepeatingOptionsFromSlots(
  selectableOptions: SelectableOption[],
  savedSlotIds: number[]
): SelectableOption[] {
  if (!savedSlotIds?.length || !selectableOptions?.length) {
    return [];
  }

  const savedSlotSet = new Set(savedSlotIds);
  const selectedOptions: SelectableOption[] = [];

  for (const option of selectableOptions) {
    // Calculate the day of week (0-6) for this option's date
    const dayIndex = moment.utc(option.date).day();

    // Generate slot IDs for this option's time range using the same
    // calculation as the timeslots feature
    const slotsInOption: number[] = [];

    // Calculate slots for each half-hour in the option's range
    let currentHour = option.startHour;
    let currentMinute = option.startMinute;

    while (
      currentHour < option.endHour ||
      (currentHour === option.endHour && currentMinute < option.endMinute)
    ) {
      const slotId = calculateTimeslotId({
        dayIndex,
        hour: currentHour,
        minute: currentMinute,
      });
      slotsInOption.push(slotId);

      // Move to next half-hour
      currentMinute += 30;
      if (currentMinute >= 60) {
        currentMinute = 0;
        currentHour += 1;
      }
    }

    // Check if any of the option's slots are in the saved slots
    const hasOverlap = slotsInOption.some((slotId) => savedSlotSet.has(slotId));

    if (hasOverlap) {
      selectedOptions.push(option);
    }
  }

  return selectedOptions;
}

export default getSelectedRepeatingOptionsFromSlots;
